import React, { useEffect } from "react";

import { isEmpty, isNil, prop } from "ramda";

import { PROCESS_STATUS } from "constants/process";
import { usePermissions } from "contexts/permissionCtx";
import { diffFromNow } from "helpers/TimeFormatter";
import { useAlerts, useUser, useUserData } from "hooks";
import { useHasPermission } from "hooks/useHasPermission";

import { Alert } from "./Alert";
import { EddAlert } from "./EddAlert";
import { EddRejectionAlert } from "./EddRejectionAlert";
import { KycAlert } from "./KycAlert";
import { RejectionAlert } from "./RejectionAlert";
import { RemediationAlert } from "./RemediationAlert";
import { UkRestrictedPromotionAlert } from "./UkRestrictedPromotionAlert";

type AlertsContainerProps = {
  hideCertificationAlters?: boolean;
  hideVerificationAlerts?: boolean;
};

type CertificationHooksArgs = {
  hideCertificationAlters?: boolean;
  hideVerificationAlerts?: boolean;
};

export const AlertsContainer = ({ hideCertificationAlters, hideVerificationAlerts }: AlertsContainerProps) => {
  const { alerts, closeAlert } = useAlerts();

  useRemediationAlert();
  useEddAlerts();
  useKycAlerts({ hideVerificationAlerts });
  useVirtualAssetsAlerts({ hideCertificationAlters });
  usePromotionAlerts();
  useSelfCertificationAlerts({ hideCertificationAlters });
  useFullCertificationAlerts({ hideCertificationAlters });

  return (
    <>
      {alerts.filter(prop("isVisible")).map(({ id, Element, props }) => {
        return (
          <Element
            {...props}
            key={id}
            onClose={(e) => {
              closeAlert(id);
              props?.onClose?.(e);
            }}
          />
        );
      })}
    </>
  );
};

const useRemediationAlert = () => {
  const { data: userData } = useUserData();
  const isRemediationRequired = useHasPermission("isRemediationRequired");
  const { addAlert, removeAlert } = useAlerts();
  const remediationKey = "remediation-alert";

  useEffect(() => {
    if (isNil(userData)) return;

    const removeAlertFn = () => removeAlert(remediationKey);
    removeAlertFn();

    if (isRemediationRequired) {
      addAlert(remediationKey, RemediationAlert, {});
      return removeAlertFn;
    }
  }, [userData, isRemediationRequired]);
};

const useEddAlerts = () => {
  const { data: userData } = useUserData();
  const {
    permissions: { isEDDProcessing, isRemediationRequired },
  } = usePermissions();

  const { addAlert, removeAlert } = useAlerts();
  const eddKey = "edd-alert";

  useEffect(() => {
    if (isNil(userData) || isRemediationRequired) return;

    const removeAlertFn = () => removeAlert(eddKey);

    removeAlertFn();

    const { edd } = userData;

    if (edd.currentStatus === PROCESS_STATUS.rejected) {
      addAlert(eddKey, EddRejectionAlert, {
        status: edd.currentStatus,
        rejectionReasons: edd.rejectionReasons,
      });
      return removeAlertFn;
    }

    if (isEDDProcessing) {
      addAlert(eddKey, EddAlert, {
        status:
          edd.currentStatus !== PROCESS_STATUS.accepted &&
          edd.currentStatus !== PROCESS_STATUS.expired &&
          edd.lastStatus !== PROCESS_STATUS.draft
            ? edd.currentStatus
            : edd.lastStatus,
        rejectionReasons: edd.rejectionReasons,
      });
      return removeAlertFn;
    }
  }, [userData, isEDDProcessing, isRemediationRequired]);
};

const usePromotionAlerts = () => {
  const { data: userData } = useUserData();
  const {
    permissions: { isPromotionRequestProcessing, isEDDBlocking, isRemediationRequired },
  } = usePermissions();
  const { addAlert, removeAlert } = useAlerts();
  const key = "promotion-alert";

  useEffect(() => {
    if (isNil(userData)) return;

    const removeAlertFn = () => removeAlert(key);

    removeAlertFn();

    if (isPromotionRequestProcessing && !isEDDBlocking && !isRemediationRequired) {
      addAlert(key, UkRestrictedPromotionAlert);

      return removeAlertFn;
    }
  }, [userData, isPromotionRequestProcessing, isEDDBlocking, isRemediationRequired]);
};

const useKycAlerts = ({ hideVerificationAlerts }: CertificationHooksArgs) => {
  const key = "kyc-alert";
  const aboutToExpireKey = key + "-about-to-expire";
  const expiredKey = key + "-expired";

  const { data: userData } = useUser();
  const {
    permissions: {
      isEDDProcessing,
      isVerificationBlocked,
      isEDDBlocking,
      isKycRejected,
      isKycAboutToExpired,
      isKycExpired,
      isRemediationRequired,
    },
  } = usePermissions();

  const { kyc } = userData;

  const { addAlert, removeAlert } = useAlerts();

  const removeAlerts = () => {
    removeAlert(key);
    removeAlert(aboutToExpireKey);
    removeAlert(expiredKey);
  };

  useEffect(() => {
    if (isEDDBlocking || isRemediationRequired) {
      removeAlerts();

      return;
    }

    if (isVerificationBlocked) {
      addAlert(key, Alert, {
        type: "error",
        message: "ID Verification Blocked",
        description: (
          <span>
            Max attempts reached: Identity Verification Blocked.{" "}
            <a href={process.env.REACT_APP_CORE_SUPPORT} target='_blank' rel='noreferrer'>
              Please contact support
            </a>
            .
          </span>
        ),
      });
      return;
    }

    removeAlerts();

    if (!hideVerificationAlerts) {
      if (isKycRejected && kyc) {
        addAlert(key, RejectionAlert, {
          rejectionReasons: kyc.rejectionReasons,
          header: "Your last ID verification request has been rejected",
          description: "Find out why your verification session was rejected by clicking the dropdown button below",
        });
      }

      if (isKycAboutToExpired) {
        const currentDate = new Date();
        const expiresAtDate = new Date(userData.kyc.expiresAt);

        if (expiresAtDate.getTime() < currentDate.getTime()) {
          addAlert(aboutToExpireKey, KycAlert, {
            status: "about-to-expired-past",
            titleParam: diffFromNow(userData.kyc.expiresAt),
          });
        } else {
          addAlert(aboutToExpireKey, KycAlert, {
            status: "about-to-expired",
            titleParam: diffFromNow(userData.kyc.expiresAt),
          });
        }
      }

      if (isKycExpired) {
        addAlert(key + "-expired", KycAlert, { status: "expired" });
      }
    }
  }, [
    kyc,
    userData,
    isEDDProcessing,
    isVerificationBlocked,
    isEDDBlocking,
    isKycRejected,
    isKycAboutToExpired,
    isKycExpired,
    isRemediationRequired,
  ]);
};

const useVirtualAssetsAlerts = ({ hideCertificationAlters }: CertificationHooksArgs) => {
  const { data: userData } = useUser();
  const {
    permissions: { isEDDBlocking, isRemediationRequired },
  } = usePermissions();

  const { virtualAssets } = userData;
  const { addAlert, removeAlert } = useAlerts();

  useEffect(() => {
    if ((!hideCertificationAlters && isNil(userData)) || isEDDBlocking || isRemediationRequired) return;
    const key = "va-pending";

    if (
      (!hideCertificationAlters && virtualAssets.currentStatus === "rejected") ||
      virtualAssets.lastStatus === "rejected"
    ) {
      addAlert(key, RejectionAlert, {
        rejectionReasons: virtualAssets.rejectionReasons,
        header: "Suitability Information Submission Rejected",
        description: "Find out why your Suitability session was rejected by clicking the dropdown button below",
      });
      return;
    } else {
      removeAlert(key);
    }
  }, [virtualAssets]);
};

const useFullCertificationAlerts = ({ hideCertificationAlters }: CertificationHooksArgs) => {
  const { data: userData } = useUser();
  const {
    permissions: { isEDDBlocking, isRemediationRequired },
  } = usePermissions();
  const basePromotionRequestRequired = useHasPermission("basePromotionRequestRequired");

  const { addAlert, removeAlert } = useAlerts();
  const { fullCertification } = userData;

  const forcedCertificationExpiration = userData.forceSelfCertification;

  useEffect(() => {
    const key = "full-certification-rejection-alert";

    if (
      !hideCertificationAlters &&
      !forcedCertificationExpiration &&
      (fullCertification.currentStatus === "expired" || fullCertification.previousStatus === "expired") &&
      fullCertification.lastStatus !== "pending"
    ) {
      addAlert(key, RejectionAlert, {
        description:
          "The documents previously used to certify your investor status have expired, and your status has been updated to Self-Certification. To maintain your Certification status, kindly submit a new application.",
        header: "Full Certification Expired:",
      });

      return;
    }

    if (
      (!hideCertificationAlters && isNil(fullCertification)) ||
      isEDDBlocking ||
      isRemediationRequired ||
      fullCertification.lastStatus === "pending" ||
      basePromotionRequestRequired
    ) {
      removeAlert(key);
      return;
    }

    if (
      !hideCertificationAlters &&
      !isNil(fullCertification.rejectionReasons) &&
      !isEmpty(fullCertification.rejectionReasons)
    ) {
      addAlert(key, RejectionAlert, {
        rejectionReasons: fullCertification.rejectionReasons,
        description: "Find out why your Certification session was rejected by clicking the dropdown button below",
        header: "Full Certification Submission Rejected",
      });

      return;
    }

    removeAlert(key);
  }, [userData, isEDDBlocking, isRemediationRequired]);
};

const useSelfCertificationAlerts = ({ hideCertificationAlters }: CertificationHooksArgs) => {
  const { data: userData } = useUser();
  const {
    permissions: { isEDDBlocking, isRemediationRequired },
  } = usePermissions();
  const { addAlert, removeAlert } = useAlerts();
  const basePromotionRequestRequired = useHasPermission("basePromotionRequestRequired");

  useEffect(() => {
    if (isEDDBlocking || isRemediationRequired || basePromotionRequestRequired) return;

    const key = "self-certification-rejection-alert";
    if (
      !hideCertificationAlters &&
      userData.forceSelfCertification &&
      userData.selfCertification.currentStatus === "expired"
    ) {
      addAlert(key, RejectionAlert, {
        description:
          "As per our policy, we require you to resubmit a self-certification before advancing to the Full certification process.",
        header: "Self-Certification Resubmission Required",
      });
      return;
    }

    removeAlert(key);
  }, [userData, isEDDBlocking, isRemediationRequired]);
};
